<template>
  <div class="middle-page-open-app__root">
    <template v-if="isNoPermission">
      <!-- 无权限 -->
      <IconMyIconNoPermission class="middle-page-open-app__no_permission_icon"></IconMyIconNoPermission>
      <div class="middle-page-open-app__no_permission_label">您无权访问该应用</div>
    </template>
    <template v-else-if="isTrialExpires">
      <!-- 试用到期 -->
      <IconMyIconBuy class="middle-page-open-app__trial_expires_icon"></IconMyIconBuy>
      <div class="middle-page-open-app__trial_expires_label">试用已到期，请立即购买</div>
      <middle-page-pay-btn :openAppPreCheckParams="openAppPreCheckParams"></middle-page-pay-btn>
    </template>
    <template v-else-if="isComboExpires">
      <!-- 套餐到期 -->
      <IconMyIconBuy class="middle-page-open-app__combo_expires_icon"></IconMyIconBuy>
      <div class="middle-page-open-app__combo_expires_label">套餐已到期，请立即购买</div>
      <middle-page-pay-btn :openAppPreCheckParams="openAppPreCheckParams"></middle-page-pay-btn>
    </template>
    <template v-else-if="isLoadAppError">
      <middle-page-app-load-error :errorLabel="appLoadErrorLabel"></middle-page-app-load-error>
    </template>
    <template v-else-if="isNetworkError">
      <!-- 网络错误 -->
      <IconMyIconNetworkError class="middle-page-open-app__network_error_icon"></IconMyIconNetworkError>
      <div class="middle-page-open-app__combo_expires_label">网络不给力，请刷新重试</div>
      <base-button type="primary" class="middle-page-open-app__network_error_refresh_btn" @click="onBtnRefreshClick"
        >刷新</base-button
      >
    </template>
    <template v-else>
      <!-- 正常 -->
      <div class="middle-page-open-app__circle">
        <el-progress class="creating-app" type="circle" :percentage="pctVal" :width="64" :stroke-width="4.5">
          <div class="middle-page-open-app__pct">{{ pctVal }}%</div>
        </el-progress>
      </div>
      <div class="middle-page-open-app__label-1">{{ isCreateAppPage ? '正在创建应用' : '正在加载应用' }}</div>
      <div class="middle-page-open-app__label-2">应用支持二次编辑，定制化满足您的需求…</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/base-button.vue';
import {
  apiPostAppCreateBy_tpl,
  apiPostMiddle_pageAppPre_check,
  isAxiosTimeoutError,
  ReqAppCreateByTpl,
  ReqMiddlePageAppPreCheck,
  RespMiddlePageAppPreCheck,
} from '@vas2t-lowcode/api';
import { computed, defineAsyncComponent, nextTick, onMounted, ref } from 'vue';
import { onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute, useRouter } from 'vue-router';
import { routeNameMiddleApp } from './middle-page-open-app.constants';
import type { TMiddlePageAppQuery, TMiddlePageCreateAppQuery, TMiddlePageOpenAppQuery } from './middle-page-open-app.d';

const middlePagePayBtn = defineAsyncComponent(() => import('@/features/middle-page-open-app/middle-page-pay-btn.vue'));
const middlePageAppLoadError = defineAsyncComponent(
  () => import('@/features/middle-page-open-app/middle-page-app-load-error.vue')
);
/** 循环百分比 */
const pctVal = ref(1);

/** 终止循环的标记 */
const pauseFlag = ref(false);

/** 启动循环 */
const loadingStart = () => {
  const start = () => {
    const val = pctVal.value;
    window.requestAnimationFrame(() => {
      pctVal.value = val + 1;

      if (pctVal.value < 96 && !pauseFlag.value) {
        start();
      }
    });
  };

  start();
};

/** 终止loading */
const loadingEnd = () => {
  pauseFlag.value = true;
  return nextTick(() => {
    pctVal.value = 100;
  });
};

const preCheckRespData = ref<RespMiddlePageAppPreCheck>();
const isDataLoading = ref(false);
const preCheckHandler = (params: ReqMiddlePageAppPreCheck) => {
  if (isDataLoading.value) {
    return;
  }

  isDataLoading.value = true;

  const _params = {
    ...params,
    third_ws_id: decodeURIComponent(params.third_ws_id || ''),
    team_id: Number(params.team_id),
  };
  // const winOpener = window.open('about:blank', '_self');
  // window.setTimeout(() => {
  //   if (!winOpener) {
  //     return;
  //   }

  //   winOpener.location.href = `https://lcdp.wps.cn/lcdp/v1/middle_page/app/pre_check/v2?platform_id=${
  //     _params.platform_id
  //   }&third_app_id=${_params.third_app_id}&team_id=${_params.team_id}&third_ws_id=${encodeURIComponent(
  //     _params.third_ws_id
  //   )}&redirect_url=${_params.redirect_url}&mode=${_params.mode}`;
  // });

  //
  apiPostMiddle_pageAppPre_check(_params, { showRespErrMsg: false })
    .then(({ data }) => {
      preCheckRespData.value = data;

      const appUrl = data.forward_link;

      if (!appUrl) {
        console.warn('无法获取应用跳转地址');
        return;
      }

      // 设置三方cookie
      let setCookieUrl = data.cookie_set_url;
      if (setCookieUrl) {
        // await setThirdAppCookie(setCookieUrl);
        loadingEnd().then(() => {
          setCookieUrl && (location.href = setCookieUrl);
        });
      }

      // 原地跳转
      // loadingEnd().then(() => {
      //   appUrl && (location.href = appUrl);
      // });
    })
    .catch((err) => {
      handleError(err);
    })
    .finally(() => {
      loadingEnd();
      isDataLoading.value = false;
    });
};

/** 设置三方Cookie */
const setThirdAppCookie = (url: string) => {
  let _url;
  try {
    _url = new URL(url)?.href;
  } catch (err) {
    captureException(err);
  }

  // url解析失败
  if (!_url) {
    return Promise.resolve();
  }

  return new Promise((res, rej) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    iframe.onload = () => {
      document.body.removeChild(iframe);
      res(true);
    };
    iframe.onerror = () => {
      const err = new Error(`设置三方cookie失败，url: ${url}`);
      captureException(err);
      rej();
    };
    document.body.appendChild(iframe);
  });

  // 调用三方接口实现
  // return fetch(url, {
  //   method: 'POST',
  //   mode: 'cors',
  //   credentials: 'include',
  // })
  //   .then((data) => data.json())
  //   .then((data) => {
  //     if (data.result !== 'ok') {
  //       const err = new Error(`设置三方cookie失败，url: ${url}, ${JSON.stringify(data)}`);
  //       captureException(err);
  //     }
  //   })
  //   .catch((err) => {
  //     captureException(err);
  //   });
};

/** 无权限 */
const isNoPermission = ref(false);
/** 试用到期 */
const isTrialExpires = ref(false);
/** 套餐到期 */
const isComboExpires = ref(false);
/** 网络错误 */
const isNetworkError = ref(false);
/** 加载APP错误 */
const isLoadAppError = ref(false);
/** 加载应用失败提示 */
const appLoadErrorLabel = ref<string | undefined>(undefined);

/** 处理错误 */
const handleError = (err: any) => {
  // 超时判断
  if (isAxiosTimeoutError(err)) {
    isNetworkError.value = true;
    return;
  }

  const workspaceName = err?.data?.team_name || '';

  const { result } = err as Record<string, any>;
  switch (result) {
    case 'err_invalid_access_params':
      isNoPermission.value = true;
      break;
    case 'err_rights_trial_expired':
      isTrialExpires.value = true;
      break;
    case 'err_rights_paid_expired':
      isComboExpires.value = true;
      break;
    case 'err_team_member_disabled':
      isLoadAppError.value = true;
      appLoadErrorLabel.value = `您已被【${workspaceName}】禁用，无权访问应用`;
      break;
    case 'err_invalid_team_member':
      isLoadAppError.value = true;
      appLoadErrorLabel.value = `您已被移除【${workspaceName}】，无权访问应用`;
      break;
    default:
      isLoadAppError.value = true;
      break;
  }
};

/** 页面类型：open/create 创建应用/打开应用 */
const pageType = ref<'open' | 'create' | ''>('');

/** 当前是：创建app中间页 */
const isCreateAppPage = computed(() => {
  return pageType.value === 'create';
});

/** 当前是：打开app中间页 */
// const isOpenAppPage = computed(() => {
//   return pageType.value === 'open';
// });

/** 路由参数 */
const routeQuery = ref<TMiddlePageAppQuery>();

/** 初始化路由参数 */
const initRouteQuery = (routeParam: RouteLocationNormalizedLoaded) => {
  const queryVal = routeParam.query as any as TMiddlePageAppQuery;
  if (queryVal.page_type === 'open') {
    // 打开应用
    routeQuery.value = { ...queryVal, redirect_url: queryVal.cb } as TMiddlePageOpenAppQuery;
  } else {
    // 创建应用
    routeQuery.value = { ...queryVal } as TMiddlePageCreateAppQuery;
  }

  // 更新页面类型
  pageType.value = queryVal.page_type || '';
};

const router = useRouter();

const toOpenAppPage = (queryData: Record<string, any>) => {
  router.replace({ name: routeNameMiddleApp, query: queryData });
};

onBeforeRouteUpdate((to) => {
  /**
   * 解决新建应用->打开应用时不刷新页面，可显示打开应用页
   *
   * 路由为同一个，不触发mounted，只触发onBeforeRouteUpdate，所以需要手动刷新数据
   *
   */
  initRouteQuery(to);
  initData();
});

/** 数据初始化 */
const initData = () => {
  const queryVal = routeQuery.value;

  if (!queryVal) {
    return;
  }

  // 同步相关数据并执行逻辑
  if (queryVal.page_type === 'create') {
    createAppParams.value = queryVal;
    // 创建应用
    // https://dev.wps.cn/#/middle-page-open-app?page_type=open&platform_id=AK20210518HIRGVN&team_id=75&third_app_id=1428099&third_ws_id=tAeLHd46LCK7%252F63p1EdZIw%253D%253D&tpl_id=69150886
    createAppHandler(createAppParams.value);
  } else if (queryVal.page_type === 'open') {
    // mode: 全校验-0 不检验工作区-1
    openAppPreCheckParams.value = { ...queryVal, redirect_url: queryVal.cb || '', mode: Number(queryVal.mode) || 0 };
    // 打开应用
    preCheckHandler(openAppPreCheckParams.value);
  }
};

/** 创建应用所需接口参数 */
const createAppParams = ref<ReqAppCreateByTpl>();
/** 打开应用所需接口参数 */
const openAppPreCheckParams = ref<TMiddlePageOpenAppQuery>();

/** app创建中状态值 */
const isCreateTemAppLoading = ref(false);
const createAppHandler = (params: ReqAppCreateByTpl) => {
  // 创建应用
  if (isCreateTemAppLoading.value) {
    return;
  }

  isCreateTemAppLoading.value = true;
  const _params = { ...params, team_id: Number(params.team_id) };
  apiPostAppCreateBy_tpl(_params, { showRespErrMsg: false, timeout: 15000 })
    .then(({ data }) => {
      // 打开新tab页
      const _data = {
        platform_id: params.platform_id,
        team_id: params.team_id,
        third_app_id: data.third_app_id,
        third_ws_id: data.ws_id,
      } as ReqMiddlePageAppPreCheck;

      toOpenAppPage({ ..._data, page_type: 'open' });
    })
    .catch((err) => {
      handleError(err);
    })
    .finally(() => {
      isCreateTemAppLoading.value = false;
    });
};

/** 刷新点击 */
const onBtnRefreshClick = () => {
  location.reload();
};

onMounted(() => {
  const route = useRoute();
  initRouteQuery(route);
  initData();
  loadingStart();
});
</script>

<style lang="scss" scoped>
//

.middle-page-open-app {
  &__root {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  &__circle {
    display: inline-block;
    width: 64px;
    height: 64px;
    position: relative;
  }

  &__pct {
    height: 18px;
    font-size: 16px;
    font-weight: bold;
    color: var(--lowcode-theme-common-01);
    line-height: 18px;
  }

  &__label-1 {
    margin-top: 12px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }

  &__label-2 {
    margin-top: 2px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-04);
    line-height: 22px;
  }

  &__no_permission_label {
    margin-top: 12px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }

  &__trial_expires_label {
    margin-top: 12px;
    height: 22px;
    font-size: 12px;
    font-family: MicrosoftYaHei;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }

  &__combo_expires_label {
    margin-top: 12px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }

  &__network_error_label {
    margin-top: 12px;
    height: 22px;
    font-size: 12px;
    color: var(--lowcode-text-02);
    line-height: 22px;
  }
  &__network_error_refresh_btn {
    margin-top: 16px;
  }
}
</style>

<style lang="scss">
.creating-app {
  .el-progress-circle__path {
    stroke: var(--lowcode-theme-common-01) !important;
  }
}
</style>
